import { Injectable } from '@angular/core';
import { FilterQuery } from '@alfresco/adf-content-services';
import { QueryBody, RequestFacetFields, SearchRequest } from '@alfresco/js-api';
import { BehaviorSubject, Subject } from 'rxjs';
import { UserPreferencesService, UserPreferenceValues } from '@alfresco/adf-core';
import { DateAdapter } from '@angular/material/core';
import { Moment } from 'moment';
import { CustomDataColumn, CustomViewerConfig } from '../models/custom-viewer-config';
import { ExtensionService } from '@alfresco/adf-extensions';
import { CustomFilterQuery } from '../models/custom-filter-query';
import { CustomRuleContext } from '../models/custom-rule-context';

@Injectable({
  providedIn: 'root'
})
export class CustomListQueryBuilderService {
  error = new Subject();
  columns: CustomDataColumn[];
  paging: { maxItems?: number; skipCount?: number } = null;
  currentQuery: SearchRequest = null;

  viewerConfig: CustomViewerConfig;
  defaultFilterQueries: FilterQuery[] = [];
  baseQuery: string = '';
  facetFields: RequestFacetFields;

  private filterQueriesSubject = new BehaviorSubject<CustomFilterQuery[]>([]);
  filterQueries$ = this.filterQueriesSubject.asObservable();

  constructor(private readonly dateAdapter: DateAdapter<Moment>, private readonly userPreferencesService: UserPreferencesService, protected extensions: ExtensionService) {
    this.userPreferencesService.select(UserPreferenceValues.Locale).subscribe((locale) => {
      this.dateAdapter.setLocale(locale);
    });
  }

  /**
 * Carga la configuración del visor y aplica las columnas y consultas por defecto.
 *
 * Este método inicializa el servicio a partir de la configuración proporcionada
 * por el visor (`viewerConfig`). A partir de dicha configuración se establecen:
 *
 *  - Las columnas visibles en la tabla.
 *  - Los filtros iniciales.
 *  - Las consultas por defecto que deben aplicarse al construir la query.
 *
 * Su propósito es preparar el servicio para generar consultas coherentes con la
 * configuración del visor antes de que se soliciten datos.
 *
 * @param viewerConfig Configuración personalizada del visor que define columnas,
 *                     filtros y consultas por defecto.
 */
  loadConfiguration(viewerConfig: CustomViewerConfig): void {
    this.viewerConfig = viewerConfig;
    this.defaultFilterQueries = this.viewerConfig.searchConfig.defaultFilterQueries;
    this.baseQuery = this.viewerConfig.searchConfig.baseQuery;
    this.columns = (this.viewerConfig.columns || []).filter((column) => column.search.enabled && (!column.visible || column.visible == "true" || (column.visible != "false" && this.extensions.evaluateRule(column.visible))));
    this.facetFields = this.viewerConfig.searchConfig.facetFields;
  }

  /**
   * Construye la query que se enviará al backend para realizar la búsqueda.
   *
   * La `baseQuery` solo debe utilizarse para filtrar propiedades simples, ya que
   * no permite filtrar por TYPE, ASPECT u otros elementos estructurales del
   * repositorio. Por este motivo, normalmente se deja vacía y se aplican los
   * filtros iniciales (como TYPE o ASPECT) mediante `filterQueries`.
   *
   * Si `baseQuery` está vacía, se establece automáticamente como `"*"` para
   * evitar errores y asegurar que la búsqueda siempre tenga un criterio válido.
   *
   * El método genera un objeto `QueryBody` que incluye:
   *  - La query base (`baseQuery`)
   *  - Los filtros obtenidos mediante `getBasicFilterQueries()`
   *  - La configuración de paginación
   *  - Los campos a incluir en la respuesta
   *  - El ordenamiento (`sorting`)
   *  - Los facet fields configurados
   *
   * Finalmente, la query construida se almacena en `currentQuery`.
   *
   * @param sorting Configuración de ordenación aplicada a la consulta.
   */
  buildQuery(sorting): void {
    // Por si la dejamos vacía, se pone por defecto un *
    if (!this.baseQuery) {
      this.baseQuery = "*";
    }

    const filterQueries = this.getBasicFilterQueries();
    const result: QueryBody = {
      query: {
        query: this.baseQuery,
        language: 'afts'
      },
      include: this.viewerConfig.searchConfig.include,
      paging: this.paging,
      filterQueries: filterQueries,
      sort: sorting,
      facetFields: this.facetFields
    };
    this.currentQuery = result;
  }

  /**
   * Devuelve la query actual construida por el servicio.
   *
   * Este método permite acceder a la última consulta generada mediante
   * `buildQuery`, incluyendo filtros, paginación, ordenación y configuración
   * del visor.
   *
   * @returns La query actual almacenada en `currentQuery`.
   */
  getCurrentQuery() {
    return this.currentQuery;
  }

  /**
   * Establece la configuración de paginación para la consulta.
   *
   * Este método actualiza los valores de `maxItems` y `skipCount` utilizados
   * al construir la query. Solo modifica la paginación si los valores han
   * cambiado, evitando reconstrucciones innecesarias.
   *
   * @param maxItems Número máximo de elementos por página.
   * @param skipCount Número de elementos a omitir (offset) en la consulta.
   */
  setPagination(maxItems: number, skipCount: number) {
    if (!this.paging || (this.paging && this.paging.maxItems !== maxItems) || this.paging.skipCount !== skipCount) {
      this.paging = { maxItems, skipCount };
    }
  }

  /**
   * Obtiene la definición de una columna a partir de su clave.
   *
   * Este método busca dentro de la configuración de columnas del visor
   * (`this.columns`) la columna cuyo `key` coincida con el proporcionado.
   * Si no existe o no se han cargado columnas, devuelve `null`.
   *
   * @param columnKey Clave única de la columna que se desea recuperar.
   * @returns La columna encontrada o `null` si no existe.
   */
  public getColumn(columnKey: string): CustomDataColumn {
    let columns = null;
    if (this.columns !== null) {
      columns = this.columns.find((columns) => columns.key === columnKey) ?? null;
    }
    return columns;
  }

  /** FILTER QUERYS **/

  /**
   * Obtiene los filtros actuales en su formato básico para la búsqueda.
   *
   * Este método transforma la lista interna de `CustomFilterQuery` en un array
   * de `FilterQuery`, extrayendo únicamente la propiedad `query` de cada filtro.
   * Este formato es el que Alfresco espera en la propiedad `filterQueries` de
   * una consulta AFTS.
   *
   * @returns Lista de filtros en formato básico (`FilterQuery[]`).
   */
  getBasicFilterQueries(): FilterQuery[] {
    const filterQueries: FilterQuery[] = [];
    this.getFilterQueries().forEach(filterQuery => {
      filterQueries.push(filterQuery.query);
    });
    return filterQueries;
  }

  /**
   * Devuelve la lista actual de filtros personalizados.
   *
   * Los filtros se almacenan en un `BehaviorSubject`, lo que permite que otros
   * componentes o servicios reaccionen a los cambios. Este método devuelve el
   * valor actual sin necesidad de suscribirse.
   *
   * @returns Lista de filtros personalizados (`CustomFilterQuery[]`).
   */
  getFilterQueries(): CustomFilterQuery[] {
    return this.filterQueriesSubject.value;
  }

  /**
   * Establece los filtros por defecto definidos en la configuración del visor.
   *
   * Este método recorre la lista `defaultFilterQueries` y los añade como filtros
   * activos mediante `addFilterQuery`. Se utiliza normalmente al inicializar el
   * visor o al restablecer los filtros.
   */
  setDefaultFilterQueries() {
    this.defaultFilterQueries?.forEach(filterQuery => {
      this.addFilterQuery({ id: 'customDefault', query: filterQuery });
    });
  }

  /**
   * Reemplaza completamente la lista actual de filtros.
   *
   * Este método actualiza el `BehaviorSubject` con una nueva lista de filtros,
   * notificando automáticamente a todos los suscriptores.
   *
   * @param filterQueries Nueva lista de filtros personalizados.
   */
  setFilterQueries(filterQueries: CustomFilterQuery[]) {
    this.filterQueriesSubject.next(filterQueries);
  }

  /**
   * Añade un nuevo filtro a la lista de filtros activos.
   *
   * Este método obtiene la lista actual de filtros, agrega el nuevo filtro y
   * actualiza el estado mediante `setFilterQueries`.
   *
   * @param filterQuery Filtro que se desea añadir.
   */
  addFilterQuery(filterQuery: CustomFilterQuery) {
    const currentQueries = this.getFilterQueries();
    this.setFilterQueries([...currentQueries, filterQuery]);
  }

  /**
   * Elimina un filtro de la lista de filtros activos.
   *
   * El filtro se identifica por su propiedad `id`. Si existe, se elimina y se
   * actualiza la lista de filtros.
   *
   * @param filterQueryId Identificador del filtro a eliminar.
   */
  removeFilterQuery(filterQueryId: any) {
    const currentQueries = this.getFilterQueries();
    this.setFilterQueries(currentQueries.filter(filterQuery => filterQuery.id !== filterQueryId));
  }

  /**
   * Restablece los filtros actuales y aplica los filtros por defecto.
   *
   * Por defecto, elimina únicamente los filtros editables y conserva aquellos
   * que no pueden ser modificados por el usuario. Si `clearAll` es `true`,
   * elimina todos los filtros actuales independientemente de si son editables
   * o no.
   *
   * @param clearAll Indica si se deben eliminar todos los filtros actuales.
   * Por defecto, solo se eliminan los filtros editables.
   */
  clearAndSetDefaultFilterQueries(clearAll = false) {
    const currentQueries = this.getFilterQueries();
    
    const clearedQueries = clearAll ? [] : currentQueries.filter(query => {
      const filter = this.columns.find(filter => filter.search.settings?.field === query.id );
      if(filter) {
        const context = { filter } as CustomRuleContext;
        const isNotEditable =
          filter?.search?.editable &&
          !this.extensions.evaluateRule(filter.search.editable, context);
        return isNotEditable;
      }
      return false;
    });

    this.setFilterQueries(clearedQueries);
    this.setDefaultFilterQueries();
  }
}
